import { Fragment } from "react";
import { ArrowLeft, Check, X, Shield, Sparkles, Building } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const HEADER_LOGO_URL = "/header_logo.jpeg";

// Feature matrix sourced from /app/docs/products/PRODUCT_COMPARISON.md
const FEATURE_GROUPS = [
  {
    group: "Analysis Capabilities",
    rows: [
      { feat: "Single floor analysis", lite: true, pro: true, plus: true },
      { feat: "Multi-floor analysis", lite: false, pro: "Up to 10 floors", plus: "Unlimited" },
      { feat: "ZIP / tender bundle upload (drawings + BOQ + NOC)", lite: false, pro: true, plus: true },
      { feat: "Multi-page PDF auto-splitter (one page = one floor)", lite: false, pro: true, plus: true },
      { feat: "DWG auto-conversion to PDF (CloudConvert)", lite: false, pro: true, plus: true },
      { feat: "Max file size", lite: "10 MB", pro: "50 MB", plus: "200 MB" },
      { feat: "Monthly analyses included", lite: "5", pro: "5", plus: "5 (then volume)" },
      { feat: "Project-scope smart scaling (1 floor → whole building)", lite: true, pro: true, plus: true },
    ],
  },
  {
    group: "Symbol Detection",
    rows: [
      { feat: "Sprinklers, hydrants, landing valves, detectors, MCPs, extinguishers", lite: true, pro: true, plus: true },
      { feat: "Fittings (elbows, tees, reducers, flanges, couplings, unions)", lite: false, pro: true, plus: true },
      { feat: "Legend-driven detection (auto-read drawing legend + custom symbols)", lite: false, pro: false, plus: true },
    ],
  },
  {
    group: "Pipe Analysis",
    rows: [
      { feat: "Pipe length total", lite: false, pro: true, plus: true },
      { feat: "Pipe length BY DIAMETER (25 / 32 / 40 / 50 / 65 / 80 / 100 / 150 Ø)", lite: false, pro: false, plus: true },
      { feat: "Auto scale detection from title block + DXF units", lite: false, pro: true, plus: true },
    ],
  },
  {
    group: "Compliance & Gap Analysis",
    rows: [
      { feat: "NBC 2016 + 2026 full check", lite: true, pro: true, plus: true },
      { feat: "27 region codes (India 15 states + UAE, Qatar, KSA, USA, UK, SG, etc.)", lite: true, pro: true, plus: true },
      { feat: "NFPA 13 / 14 / 20 / 72 + IS standards", lite: true, pro: true, plus: true },
      { feat: "Super-High-Rise checks (>70 m)", lite: false, pro: true, plus: true },
      { feat: "Architect-BOQ vs Tool gap analysis", lite: false, pro: true, plus: true },
      { feat: "Fire NOC vs BOQ third-axis comparator", lite: false, pro: true, plus: true },
      { feat: "Hazard classification (Light / Ordinary / Extra / Special)", lite: true, pro: true, plus: true },
    ],
  },
  {
    group: "Exports",
    rows: [
      { feat: "View online dashboard", lite: true, pro: true, plus: true },
      { feat: "CSV export", lite: false, pro: true, plus: true },
      { feat: "Excel export (multi-sheet workbook)", lite: false, pro: true, plus: true },
      { feat: "PDF report (professional formatted)", lite: false, pro: true, plus: true },
      { feat: "White-label / custom-branded reports", lite: false, pro: false, plus: true },
      { feat: "API access + webhooks", lite: false, pro: false, plus: true },
    ],
  },
  {
    group: "Support",
    rows: [
      { feat: "FAQ + documentation", lite: true, pro: true, plus: true },
      { feat: "Email support", lite: false, pro: "48 hr SLA", plus: "4 hr SLA" },
      { feat: "Phone support", lite: false, pro: false, plus: true },
      { feat: "Dedicated account manager", lite: false, pro: false, plus: true },
      { feat: "Onboarding training", lite: false, pro: false, plus: true },
    ],
  },
];

function Cell({ v }) {
  if (v === true) return <Check className="w-4 h-4 mx-auto text-emerald-600" />;
  if (v === false) return <X className="w-4 h-4 mx-auto text-slate-300" />;
  return <span className="text-xs text-slate-700 font-medium">{v}</span>;
}

const TIERS = [
  {
    key: "lite",
    name: "Lite",
    icon: Shield,
    tagline: "For individuals, students, and single-floor projects",
    price: "₹2,000",
    cadence: "/month",
    cta: "Get Started",
    accent: "from-slate-50 to-white",
    badge: null,
    perks: [
      "5 analyses per month",
      "Single floor analysis",
      "All simple symbol detection",
      "Full NBC + 27 region compliance",
      "Online dashboard view",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    icon: Sparkles,
    tagline: "For consultants and MEP contractors",
    price: "₹8,000",
    cadence: "/month",
    cta: "Upgrade to Pro",
    accent: "from-blue-50 to-cyan-50",
    badge: "Most popular",
    perks: [
      "Everything in Lite, plus:",
      "Multi-floor (up to 10) + ZIP tender bundle upload",
      "Fittings detection + total pipe length",
      "DWG auto-conversion, multi-page PDF splitter",
      "Architect-BOQ + Fire NOC gap comparator",
      "Excel, CSV, PDF, Full-PDF exports",
      "Super-High-Rise (>70 m) auto-checks",
    ],
  },
  {
    key: "plus",
    name: "Pro Plus",
    icon: Building,
    tagline: "For enterprises and government tenders",
    price: "Contact",
    cadence: "Sales",
    cta: "Talk to Sales",
    accent: "from-amber-50 to-yellow-50",
    badge: "Enterprise",
    perks: [
      "Everything in Pro, plus:",
      "Unlimited floors + buildings",
      "Legend-driven detection (custom symbols)",
      "Pipe length BY DIAMETER",
      "White-label / branded reports",
      "API access + webhook integrations",
      "Dedicated account manager + 4-hr SLA",
    ],
  },
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-testid="pricing-page">
      {/* Header — mirrors landing/FAQ */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={HEADER_LOGO_URL} alt="Kavach Logo" className="h-14 w-14 object-contain rounded-full" />
            <div>
              <h1 className="text-xl font-bold" style={{ color: "#003366" }}>KAVACH INFRA SOLUTIONS LLP</h1>
              <p className="text-xs" style={{ color: "#D4AF37", fontWeight: 600 }}>रक्षा | सुरक्षा | सजगता</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => (window.location.href = "/")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ color: "#003366" }}>
            Simple, honest pricing
          </h1>
          <p className="text-base max-w-2xl mx-auto text-slate-600">
            Three tiers. No hidden surcharges. Cancel any time.
            Every plan ships with the full NBC + 27-region compliance engine — only depth, exports, and scale change.
          </p>
        </div>

        {/* Tier cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16" data-testid="pricing-cards">
          {TIERS.map((t) => {
            const Icon = t.icon;
            return (
              <Card
                key={t.key}
                data-testid={`pricing-card-${t.key}`}
                className={`border-2 shadow-xl bg-gradient-to-br ${t.accent} ${
                  t.key === "pro" ? "border-blue-400 ring-2 ring-blue-200 scale-[1.02]" : "border-slate-200"
                }`}
              >
                <CardContent className="pt-6 pb-6 relative">
                  {t.badge && (
                    <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-blue-600 text-white text-[11px] font-bold uppercase tracking-wide">
                      {t.badge}
                    </div>
                  )}
                  <Icon className="w-8 h-8 mb-3" style={{ color: "#003366" }} />
                  <h3 className="text-2xl font-bold" style={{ color: "#003366" }}>{t.name}</h3>
                  <p className="text-sm text-slate-600 mt-1 mb-4">{t.tagline}</p>
                  <div className="flex items-baseline gap-1 mb-5">
                    <span className="text-4xl font-bold" style={{ color: "#003366" }}>{t.price}</span>
                    <span className="text-sm text-slate-500">{t.cadence}</span>
                  </div>
                  <Button
                    onClick={() => (t.key === "plus"
                      ? (window.location.href = "mailto:info@kavachinfra.in?subject=Pro%20Plus%20enquiry")
                      : window.open("https://app.kavachinfra.in", "_blank"))}
                    className="w-full mb-5"
                    style={{ backgroundColor: t.key === "pro" ? "#003366" : "#0f172a" }}
                    data-testid={`cta-${t.key}`}
                  >
                    {t.cta}
                  </Button>
                  <ul className="space-y-2">
                    {t.perks.map((p, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <Check className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Detailed comparison table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="pricing-table">
          <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
            <h3 className="text-lg font-bold" style={{ color: "#003366" }}>Detailed feature comparison</h3>
            <p className="text-xs text-slate-500 mt-1">Cells with text indicate quantitative limits, ✓ means included, blank means not available.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700 w-1/2">Feature</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-700">Lite</th>
                  <th className="text-center px-4 py-3 font-semibold text-blue-700 bg-blue-50">Pro</th>
                  <th className="text-center px-4 py-3 font-semibold text-amber-700 bg-amber-50">Pro Plus</th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_GROUPS.map((g, gi) => (
                  <Fragment key={`g-${gi}`}>
                    <tr>
                      <td colSpan={4} className="px-4 py-2 bg-slate-100 text-xs font-bold uppercase tracking-wide text-slate-600">
                        {g.group}
                      </td>
                    </tr>
                    {g.rows.map((r, ri) => (
                      <tr key={`r-${gi}-${ri}`} className="border-t border-slate-100">
                        <td className="px-4 py-3 text-slate-800">{r.feat}</td>
                        <td className="px-4 py-3 text-center"><Cell v={r.lite} /></td>
                        <td className="px-4 py-3 text-center bg-blue-50/40"><Cell v={r.pro} /></td>
                        <td className="px-4 py-3 text-center bg-amber-50/40"><Cell v={r.plus} /></td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-12">
          <p className="text-sm text-slate-600 mb-3">Not sure which plan fits? Email us your project size and we'll recommend.</p>
          <Button
            onClick={() => (window.location.href = "mailto:info@kavachinfra.in?subject=Pricing%20enquiry")}
            variant="outline"
            data-testid="pricing-contact-cta"
          >
            Contact sales
          </Button>
        </div>
      </main>
    </div>
  );
}
